/**
 * @file: OrderContext.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { useCurrentUser } from '@euler/app/flows/auth/context';
import { OrderDraftManager } from '@euler/app/flows/order/functions/OrderDraftManager';
import { useCreation } from '@euler/utils/hooks';
import React, {
  FC,
  memo,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { Subject } from 'rxjs';

export type OrderEventType =
  | 'created'
  | 'updated'
  | 'rollback'
  | 'finished'
  | 'canceled'
  | 'subscribed'
  | 'unsubscribed';

type OrderEventArgs = {
  type: OrderEventType;
  isMainOrder: boolean;
  mainOrderNo?: string;
  mainTaskNo?: string;
  orderNo: string;
  taskNo: string;
  extra?: any;
};

export type OrderContextType = {
  event$: Subject<OrderEventArgs>;
  draftManager: OrderDraftManager;
};

export const OrderContext = React.createContext<OrderContextType>(null as any);

export const useOrderContext = () => useContext(OrderContext);

export const OrderContextProvider: FC<{ children?: ReactNode }> = memo(
  props => {
    const user = useCurrentUser();
    const event$ = useCreation(() => new Subject<OrderEventArgs>(), []);
    const draftManager = useCreation(
      () => new OrderDraftManager(user?.org.id ?? 0, user?.store.id ?? 0),
      [user?.org.id, user?.store.id],
    );
    useEffect(() => () => draftManager.dispose(), [draftManager]);

    const value = useMemo(
      () => ({
        event$,
        draftManager,
      }),
      [event$, draftManager],
    );

    return (
      <OrderContext.Provider value={value}>
        {props.children}
      </OrderContext.Provider>
    );
  },
);
